import React, { useMemo } from 'react';
import { Skeleton, Typography } from '@mui/material';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { styled } from '@mui/system';
import { currencyFormatter } from 'helpers/formatHelper';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const StyledTypography = styled(Typography)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    columnGap: theme.spacing(1),
    rowGap: 0,
}));

const NeutralEqualityIcon = (
    <HorizontalRuleIcon
        sx={(theme) => ({ color: theme.palette.orange })}
        fontSize="small"
    />
);

const DecreasingEvolutionIcon = (
    <TrendingDownIcon
        sx={(theme) => ({ color: theme.palette.green })}
        fontSize="small"
    />
);

const IncreasingEvolutionIcon = (
    <TrendingUpIcon
        sx={(theme) => ({ color: theme.palette.red })}
        fontSize="small"
    />
);

type Props = {
    value: number;
    isLoading: boolean;
    year?: number;
    isInAccordion?: boolean;
    expanded?: boolean;
};

const CostIcon = ({
    value,
    isLoading,
    year = undefined,
    isInAccordion = false,
    expanded = false,
}: Props): React.ReactElement => {
    const { t } = useTranslation();

    const IconComponent = useMemo(() => {
        if (isLoading) {
            return null;
        }

        if (value === 0) {
            return NeutralEqualityIcon;
        }

        return value > 0 ? IncreasingEvolutionIcon : DecreasingEvolutionIcon;
    }, [value, isLoading]);

    if (isLoading) {
        return <Skeleton variant="rectangular" width={100} height={20} />;
    }

    return (
        <StyledTypography variant="h4">
            {IconComponent}
            {currencyFormatter(value)}
            <Typography
                variant="body1"
                sx={{ fontSize: '12px', display: 'flex', alignItems: 'center' }}
            >
                {year
                    ? `${t('date.byYear', { year })}`
                    : t('humanResources.simulation.detailByYear')}
                {isInAccordion &&
                    (expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
            </Typography>
        </StyledTypography>
    );
};

export default React.memo(CostIcon);
